import { ViewMode, CompletionStatus } from "../types/enums";

const STORAGE_KEY = "timeTracker_uiSettings";

interface UISettings {
  filterPeriod?: string;
  dateRange?: { start: string; end: string };
  selectedTopics?: string[];
  selectedProjects?: string[];
  selectedCompletionStatus?: CompletionStatus;
  filterBySessions?: boolean;
  viewMode?: ViewMode;
  colorSaturation?: number;
  trackInBetweenTime?: boolean;
}

class UISettingsService {
  private getSettings(): UISettings {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error("Error parsing UI settings:", error);
      }
    }
    return {};
  }

  private saveSettings(settings: UISettings): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error("Error saving UI settings:", error);
    }
  }

  getFilterSettings(): Partial<UISettings> {
    const {
      filterPeriod,
      dateRange,
      selectedTopics,
      selectedProjects,
      selectedCompletionStatus,
      filterBySessions,
    } = this.getSettings();
    return {
      filterPeriod,
      dateRange,
      selectedTopics,
      selectedProjects,
      selectedCompletionStatus,
      filterBySessions,
    };
  }

  setFilterSettings(settings: Partial<UISettings>): void {
    const current = this.getSettings();
    this.saveSettings({ ...current, ...settings });
  }

  getViewMode(): ViewMode {
    return this.getSettings().viewMode || ViewMode.COMPRESSED;
  }

  setViewMode(mode: ViewMode) {
    const settings = this.getSettings();
    settings.viewMode = mode;
    this.saveSettings(settings);
  }

  getColorSaturation(): number {
    const settings = this.getSettings();
    return settings.colorSaturation ?? 100;
  }

  setColorSaturation(saturation: number) {
    const settings = this.getSettings();
    settings.colorSaturation = saturation;
    this.saveSettings(settings);
  }

  getTrackInBetweenTime(): boolean {
    const settings = this.getSettings();
    return settings.trackInBetweenTime ?? false;
  }

  setTrackInBetweenTime(enabled: boolean) {
    const settings = this.getSettings();
    settings.trackInBetweenTime = enabled;
    this.saveSettings(settings);
  }
}

export const uiSettingsService = new UISettingsService();
